// ============================================================
// Accredited KYC - Utility Functions & Constants
// Aligned with backend accreditation APIs
// ============================================================

// Accreditation type options for display
export const ACCREDITATION_TYPES = {
  INCOME: {
    value: "INCOME",
    label: "Income-Based Verification",
    shortLabel: "Individual/Joint Income",
    description:
      "Individual income > $200,000 or joint income > $300,000 in each of the 2 most recent years",
    icon: "💰",
  },
  NET_WORTH: {
    value: "NET_WORTH",
    label: "Net Worth Verification",
    shortLabel: "Net Worth > $1M",
    description:
      "Individual or joint net worth exceeds $1,000,000 excluding primary residence",
    icon: "🏦",
  },
  PROFESSIONAL: {
    value: "PROFESSIONAL",
    label: "Professional Certification",
    shortLabel: "Series 7 / 65 / 82",
    description:
      "Holder of Series 7, Series 65, or Series 82 license in good standing",
    icon: "📜",
  },
  ENTITY: {
    value: "ENTITY",
    label: "Entity / Institutional",
    shortLabel: "Entity Assets > $5M",
    description:
      "Entity with total assets > $5,000,000, not formed solely to acquire securities",
    icon: "🏢",
  },
};

// Document requirements per accreditation type (matches backend DocumentRequirementConfig)
export const DOCUMENT_REQUIREMENTS = {
  INCOME: {
    minimumDocuments: 1,
    requiredGroups: [
      {
        groupId: "primary",
        label: "Tax Return or CPA Letter",
        hint: "At least one of these must be provided",
        codes: ["TAX_RETURN", "CPA_LETTER"],
      },
    ],
    documents: [
      {
        code: "TAX_RETURN",
        label: "Tax Return (Last 2 years)",
        required: false,
        requiredGroup: "primary",       // ← belongs to the "any one of" group
        hint: "IRS Form 1040 or equivalent",
      },
      {
        code: "CPA_LETTER",
        label: "CPA Verification Letter",
        required: false,
        requiredGroup: "primary",       // ← belongs to the "any one of" group
        hint: "CPA verification of income",
      },
      {
        code: "W2_FORM",
        label: "W-2 Form",
        required: false,
        hint: "Employment income proof",
      },
      {
        code: "FORM_1099",
        label: "1099 Form",
        required: false,
        hint: "Self-employment / freelance income",
      },
      {
        code: "EMPLOYER_LETTER",
        label: "Employer Verification Letter",
        required: false,
        hint: "Official income verification from employer",
      },
      {
        code: "THIRD_PARTY_VERIFICATION_LETTER",
        label: "Third-Party Verification Letter",
        required: false,
        hint: "From registered broker-dealer or attorney",
      },
    ],
  },
  NET_WORTH: {
    minimumDocuments: 2,
    documents: [
      {
        code: "BANK_STATEMENT",
        label: "Bank Statement",
        required: true,
        hint: "Recent (within 90 days)",
      },
      {
        code: "BROKERAGE_STATEMENT",
        label: "Brokerage Statement",
        required: false,
        hint: "Investment account statements",
      },
      {
        code: "REAL_ESTATE_APPRAISAL",
        label: "Real Estate Appraisal",
        required: false,
        hint: "Property valuation (excluding primary residence)",
      },
      {
        code: "CREDIT_REPORT",
        label: "Credit Report",
        required: false,
        hint: "To verify liabilities",
      },
      {
        code: "CPA_LETTER",
        label: "CPA Verification Letter",
        required: false,
        hint: "CPA verification of net worth",
      },
      {
        code: "THIRD_PARTY_VERIFICATION_LETTER",
        label: "Third-Party Verification Letter",
        required: false,
        hint: "From registered broker-dealer or attorney",
      },
    ],
  },
  PROFESSIONAL: {
    minimumDocuments: 1,
    documents: [
      {
        code: "CERTIFICATION_COPY",
        label: "Professional License Copy",
        required: true,
        hint: "Copy of active Series 7 / 65 / 82 license",
      },
      {
        code: "FINRA_VERIFICATION",
        label: "FINRA BrokerCheck Verification",
        required: false,
        hint: "FINRA verification printout",
      },
      {
        code: "THIRD_PARTY_VERIFICATION_LETTER",
        label: "Third-Party Verification Letter",
        required: false,
        hint: "From employing firm or attorney",
      },
    ],
  },
  ENTITY: {
    minimumDocuments: 2,
    documents: [
      {
        code: "FORMATION_DOCUMENTS",
        label: "Formation Documents",
        required: true,
        hint: "Articles of Incorporation / LLC Agreement / Trust Deed",
      },
      {
        code: "AUDITED_FINANCIALS",
        label: "Audited Financial Statements",
        required: true,
        hint: "Most recent audited financial statements",
      },
      {
        code: "OWNERSHIP_STRUCTURE",
        label: "Ownership Structure Document",
        required: false,
        hint: "Org chart / beneficial owners documentation",
      },
      {
        code: "BANK_STATEMENT",
        label: "Entity Bank Statement",
        required: false,
        hint: "Entity bank account statement (within 90 days)",
      },
      {
        code: "THIRD_PARTY_VERIFICATION_LETTER",
        label: "Third-Party Verification Letter",
        required: false,
        hint: "From attorney or CPA firm",
      },
    ],
  },
};

// Certification type options (PROFESSIONAL accreditation)
export const CERTIFICATION_TYPES = [
  { value: "SERIES_7", label: "Series 7 - General Securities Representative" },
  { value: "SERIES_65", label: "Series 65 - Investment Adviser Representative" },
  { value: "SERIES_82", label: "Series 82 - Private Securities Offerings Representative" },
];

// Entity type options (ENTITY accreditation)
export const ENTITY_TYPES = [
  { value: "LLC", label: "Limited Liability Company (LLC)" },
  { value: "CORPORATION", label: "Corporation" },
  { value: "TRUST", label: "Trust" },
  { value: "PARTNERSHIP", label: "Partnership" },
  { value: "OTHER", label: "Other" },
];

// Currency options
export const CURRENCY_OPTIONS = [
  { value: "USD", label: "USD - US Dollar" },
  { value: "EUR", label: "EUR - Euro" },
  { value: "GBP", label: "GBP - British Pound" },
  { value: "JPY", label: "JPY - Japanese Yen" },
  { value: "CNY", label: "CNY - Chinese Yuan" },
  { value: "INR", label: "INR - Indian Rupee" },
  { value: "CAD", label: "CAD - Canadian Dollar" },
  { value: "AUD", label: "AUD - Australian Dollar" },
  { value: "CHF", label: "CHF - Swiss Franc" },
  { value: "HKD", label: "HKD - Hong Kong Dollar" },
  { value: "SGD", label: "SGD - Singapore Dollar" },
  { value: "SEK", label: "SEK - Swedish Krona" },
  { value: "KRW", label: "KRW - South Korean Won" },
  { value: "NOK", label: "NOK - Norwegian Krone" },
  { value: "NZD", label: "NZD - New Zealand Dollar" },
  { value: "MXN", label: "MXN - Mexican Peso" },
  { value: "BRL", label: "BRL - Brazilian Real" },
  { value: "ZAR", label: "ZAR - South African Rand" },
  { value: "RUB", label: "RUB - Russian Ruble" },
  { value: "AED", label: "AED - UAE Dirham" },
  { value: "SAR", label: "SAR - Saudi Riyal" },
  { value: "TRY", label: "TRY - Turkish Lira" },
  { value: "IDR", label: "IDR - Indonesian Rupiah" },
  { value: "MYR", label: "MYR - Malaysian Ringgit" },
  { value: "THB", label: "THB - Thai Baht" },
  { value: "PHP", label: "PHP - Philippine Peso" },
  { value: "PLN", label: "PLN - Polish Złoty" },
  { value: "DKK", label: "DKK - Danish Krone" },
  { value: "HUF", label: "HUF - Hungarian Forint" },
  { value: "CZK", label: "CZK - Czech Koruna" },
  { value: "ILS", label: "ILS - Israeli Shekel" },
  { value: "CLP", label: "CLP - Chilean Peso" },
  { value: "EGP", label: "EGP - Egyptian Pound" },
  { value: "PKR", label: "PKR - Pakistani Rupee" },
  { value: "KWD", label: "KWD - Kuwaiti Dinar" },
  { value: "QAR", label: "QAR - Qatari Riyal" },
];

// Verification method options
export const VERIFICATION_METHODS = [
  { value: "DOCUMENT_UPLOAD", label: "Document Upload" },
  { value: "THIRD_PARTY", label: "Third-Party Verification" },
  { value: "SELF_DECLARATION", label: "Self Declaration" },
];

// Application status config for UI display
export const STATUS_CONFIG = {
  DRAFT: {
    color: "#607D8B",
    label: "Draft",
    message: "Your application is saved as a draft.",
    description: "Please complete and submit your application to proceed with verification.",
    allowContinue: true,
    allowReapply: false,
  },
  PENDING: {
    color: "#d6a32d",
    label: "Pending Review",
    message: "Accredited investor verification documents submitted.",
    description:
      "Your accredited investor documents are currently under review. This process may take up to 48 hours. Please check your email for updates.",
    allowContinue: false,
    allowReapply: false,
  },
  UNDER_REVIEW: {
    color: "#1976D2",
    label: "Under Review",
    message: "Your application is being reviewed by our team.",
    description:
      "A compliance reviewer is examining your application and documents. You will be notified once a decision is made.",
    allowContinue: false,
    allowReapply: false,
  },
  ADDITIONAL_INFO_REQUIRED: {
    color: "#FF9800",
    label: "Additional Info Required",
    message: "Additional information is needed for your application.",
    description:
      "The reviewer has requested additional information or documents. Please check the review notes and resubmit.",
    allowContinue: false,
    allowReapply: true,
  },
  APPROVED: {
    color: "#28a745",
    label: "Approved",
    message: "Accredited investor status verified.",
    description:
      "Your accredited investor status has been successfully verified. You now have access to all accredited investor opportunities on our platform.",
    allowContinue: false,
    allowReapply: false,
  },
  REJECTED: {
    color: "#dc3545",
    label: "Rejected",
    message: "Accredited investor verification declined.",
    description:
      "Your accredited investor verification was declined. Please review the rejection reason below and submit a new application with correct information.",
    allowContinue: false,
    allowReapply: true,
  },
  EXPIRED: {
    color: "#FF9800",
    label: "Expired",
    message: "Accredited investor verification has expired.",
    description:
      "Your accredited investor verification has expired. Please submit a new application with updated documents to maintain your accredited investor status.",
    allowContinue: false,
    allowReapply: true,
  },
  CANCELLED: {
    color: "#9E9E9E",
    label: "Cancelled",
    message: "Application was cancelled.",
    description: "Your previous application was cancelled. You can submit a new application below.",
    allowContinue: false,
    allowReapply: true,
  },
};

// File validation for document uploads
export const validateFile = (fileList, required = true) => {
  if (!fileList || fileList.length === 0) {
    return required ? "This file is required" : true;
  }

  const file = fileList[0];
  const validTypes = [
    "image/jpeg",
    "image/png",
    "image/jpg",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "image/jfif",
    "image/heic",
  ];

  if (!validTypes.includes(file.type)) {
    return "Invalid file type. Please upload PDF, DOC, DOCX, JPG, PNG, JFIF, or HEIC";
  }

  if (file.size > 6 * 1024 * 1024) {
    return "File size exceeds 6 MB limit";
  }

  return true;
};

// Format currency amount
export const formatCurrency = (amount, currency = "USD") => {
  if (!amount && amount !== 0) return "N/A";
  // return new Intl.NumberFormat("en-US", {
  //   style: "currency",
  //   currency: currency,
  //   minimumFractionDigits: 0,
  //   maximumFractionDigits: 0,
  // }).format(amount);
  const homeCurrencySymbol = localStorage.getItem("homeCurrencySymbol") || "$";
  const homeCurrency = localStorage.getItem("homeCurrency") || "USD";
  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
  return `${homeCurrencySymbol}${formatted}`;
};

// Format date from timestamp or ISO string
export const formatDate = (dateValue) => {
  if (!dateValue) return "N/A";
  const date = typeof dateValue === "number" ? new Date(dateValue) : new Date(dateValue);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Format file size
export const formatFileSize = (bytes) => {
  if (!bytes) return "0 B";
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

// Keyboard input helpers
export const OnlyAllowedInput = (e) => {
  const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];
  const isNumber = /^[0-9]$/.test(e.key);
  const isLetter = /^[a-zA-Z]$/.test(e.key);
  if (!allowedKeys.includes(e.key) && !isNumber && !isLetter) {
    e.preventDefault();
  }
};

export const OnlyAllowedNumbers = (e) => {
  const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "."];
  const isNumber = /^[0-9]$/.test(e.key);
  if (!allowedKeys.includes(e.key) && !isNumber) {
    e.preventDefault();
  }
};

export const allowOnlyAlphabets = (e) => {
  const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", " "];
  const isLetter = /^[a-zA-Z]$/.test(e.key);
  if (!allowedKeys.includes(e.key) && !isLetter) {
    e.preventDefault();
  }
};

// Get document requirements for a specific type
export const getDocumentRequirementsForType = (accreditationType) => {
  return DOCUMENT_REQUIREMENTS[accreditationType] || { minimumDocuments: 0, documents: [] };
};

// Get required documents only for a type
export const getRequiredDocuments = (accreditationType) => {
  const reqs = DOCUMENT_REQUIREMENTS[accreditationType];
  if (!reqs) return [];
  return reqs.documents.filter((d) => d.required);
};

// Validate if minimum documents are uploaded
export const areMinimumDocumentsUploaded = (uploadedDocs, accreditationType) => {
  const reqs = DOCUMENT_REQUIREMENTS[accreditationType];
  if (!reqs) return false;

  const uploadedCount = Object.values(uploadedDocs).filter((f) => f).length;

  // Check individually-required documents (required: true, no group)
  const hardRequired = reqs.documents.filter((d) => d.required && !d.requiredGroup);
  const allHardRequiredUploaded = hardRequired.every((d) => uploadedDocs[d.code]);

  // Check requiredGroups — at least one doc from each group must be uploaded
  const groups = reqs.requiredGroups || [];
  const allGroupsSatisfied = groups.every((group) =>
    group.codes.some((code) => uploadedDocs[code])
  );

  return allHardRequiredUploaded && allGroupsSatisfied && uploadedCount >= reqs.minimumDocuments;
};

// Add to utils.js or at the top of AccreditedFinancialInfo component

// Allow alphanumeric characters only
export const allowAlphanumeric = (e) => {
  const key = e.key;
  // Allow alphanumeric, backspace, delete, tab, arrow keys
  if (
    !/^[a-zA-Z0-9]$/.test(key) &&
    key !== "Backspace" &&
    key !== "Delete" &&
    key !== "Tab" &&
    key !== "ArrowLeft" &&
    key !== "ArrowRight"
  ) {
    e.preventDefault();
  }
};

// Allow numeric only (already exists as OnlyAllowedNumbers, but adding explicit one)
export const allowNumericOnly = (e) => {
  const key = e.key;
  // Allow numbers, backspace, delete, tab, arrow keys
  if (
    !/^[0-9]$/.test(key) &&
    key !== "Backspace" &&
    key !== "Delete" &&
    key !== "Tab" &&
    key !== "ArrowLeft" &&
    key !== "ArrowRight"
  ) {
    e.preventDefault();
  }
};